import { useCallback, useMemo } from "react";
import type { ApiError, ApiResponse } from "@entrilia/shared/api/types";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { ObjectTypeToLookupReferences } from "../../../../api/types/objectTypes";
import type { ClientUserGroupInfo } from "../../../../api/types/userManagementTypes";
import type { ApprovalConfigurationVersion, ApprovalStep } from "../../../../api/types/workflowTypes";

interface ApprovalRuleOptions {
  clientUserGroups: ClientUserGroupInfo[];
  userGroupsError: ApiError | undefined;
  isFetchingUserGroups: boolean;
  lookupOptionsByObjectType: Record<string, ObjectTypeToLookupReferences>;
  lookupOptionsUnavailable: boolean;
}

const useApprovalRuleOptions = (
  approvalConfig: ApprovalConfigurationVersion | undefined,
  editedStep: ApprovalStep | undefined,
  hasEditPermissions: boolean
): ApprovalRuleOptions => {
  const lookupObjectTypesKey = useMemo(() => {
    const visibleRules = editedStep?.rules ?? approvalConfig?.steps.flatMap((step) => step.rules) ?? [];
    return [
      ...new Set(
        visibleRules.flatMap((rule) => {
          const source = approvalConfig?.approvalRuleFieldSources.find(
            (candidate) =>
              (candidate.relatedObjectType ?? candidate.scope) === (rule.relatedObjectType ?? rule.fieldScope)
          );
          const field = source?.fields.find((candidate) => candidate.id === rule.fieldId);
          const objectType = rule.relatedObjectType ?? approvalConfig?.objectType;
          return objectType && (field?.type === "Lookup" || field?.type === "MultiLookup") ? [objectType] : [];
        })
      ),
    ]
      .sort()
      .join(",");
  }, [approvalConfig, editedStep]);

  const getLookupOptions = useCallback(async (): Promise<ApiResponse<Record<string, ObjectTypeToLookupReferences>>> => {
    const objectTypes = lookupObjectTypesKey ? lookupObjectTypesKey.split(",") : [];
    const responses = await Promise.all(objectTypes.map((objectType) => adminApi.getLookupOptions(objectType)));
    const failed = responses.find((response) => !response.success);
    return failed
      ? { success: false, error: failed.error }
      : {
          success: true,
          data: Object.fromEntries(objectTypes.map((objectType, index) => [objectType, responses[index]?.data ?? {}])),
        };
  }, [lookupObjectTypesKey]);

  const [lookupOptionsByObjectType, lookupOptionsError] = useFetch(getLookupOptions, {
    disabled: !lookupObjectTypesKey,
  });

  const getClientUserGroups = useCallback(() => adminApi.getClientUserGroups(), []);

  const [clientUserGroups, userGroupsError, { isFetching: isFetchingUserGroups }] = useFetch(getClientUserGroups, {
    disabled: !hasEditPermissions,
  });

  const sortedClientUserGroups = useMemo(
    () => [...(clientUserGroups ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [clientUserGroups]
  );

  if (lookupOptionsError) {
    logError(lookupOptionsError, "[useApprovalRuleOptions] getLookupOptions");
  }

  return {
    clientUserGroups: sortedClientUserGroups,
    userGroupsError,
    isFetchingUserGroups,
    lookupOptionsByObjectType: lookupOptionsByObjectType ?? {},
    lookupOptionsUnavailable: Boolean(lookupOptionsError),
  };
};

export default useApprovalRuleOptions;
